/**
 * Traffic link dialog payload — which campaigns it edits and what it opens on.
 * null closes the dialog: no matching group / campaign, or a link the dialog
 * cannot show here (see link-dialog-provider.ts).
 * For Media Devoted | Created By Ciro Autiero
 */

import type { TrackerId } from '@trafficpulse/shared';

import { statsTrafficProvider } from '@/features/trackers/stats-tracker';
import type { PatchTrafficProvider } from '@/hooks';
import type { TabGroup } from '@/pages/admin/SmswiserCampaignDetailPage/types';

import { linkDialogProvider } from './link-dialog-provider';
import type { StatsCampaign } from './build-tab-groups';

export interface TrafficDialogState {
  wiserIds: number[];
  mode: 'add' | 'edit';
}

export interface TrafficDialogPayload {
  wiserIds: number[];
  initialProvider: PatchTrafficProvider;
  initialTrafficCampaignId: string | null;
}

function editPayload(
  wiserIds: number[],
  tabGroups: TabGroup[],
  enabled: readonly TrackerId[],
): TrafficDialogPayload | null {
  const want = new Set(wiserIds);
  const g = tabGroups.find(
    (tg) => tg.wiserIds.length === want.size && tg.wiserIds.every((id) => want.has(id)),
  );
  if (!g) return null;
  const initialProvider = linkDialogProvider(g.trafficProvider, enabled);
  if (!initialProvider) return null;
  return { wiserIds: g.wiserIds, initialProvider, initialTrafficCampaignId: g.trafficCampaignId };
}

function addPayload(
  wiserIds: number[],
  campaigns: StatsCampaign[],
  enabled: readonly TrackerId[],
): TrafficDialogPayload | null {
  const first = campaigns.find((x) => x.id === wiserIds[0]);
  if (!first) return null;
  const initialProvider = linkDialogProvider(statsTrafficProvider(first.trafficProvider), enabled);
  if (!initialProvider) return null;
  return {
    wiserIds,
    initialProvider,
    initialTrafficCampaignId: first.trafficCampaignId ?? first.voluumCampaignId ?? null,
  };
}

export function trafficDialogPayloadFor(
  dialog: TrafficDialogState | null,
  tabGroups: TabGroup[],
  campaigns: StatsCampaign[],
  enabled: readonly TrackerId[],
): TrafficDialogPayload | null {
  if (!dialog) return null;
  if (dialog.mode === 'edit') return editPayload(dialog.wiserIds, tabGroups, enabled);
  return addPayload(dialog.wiserIds, campaigns, enabled);
}
